import { useBoolean, useToast } from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { paramsReportToSendMapping } from '@helper/budget/parameters/paramsReportSendMapping'
import { validateGroupName } from '@helper/budget/parameters/validateGroupName'
import { parseParametersBudgetData } from '@helper/grid/budget/parameters'
import { parseParametersMargeBudgetData } from '@helper/grid/budget/parameters-marge'
import budget from '@service/budget'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IBudgetParametersContext,
  IParamsToSend,
  Pagination,
  IUpdateParams
} from '@base/@types/providers/Budget/parameters'
import { reportFilter } from '@base/variables/filters/budget/parametersFilter'
import { reportRequest } from '@base/variables/filters/budget/parametersRequest'
import budgetCommercialHeader from '@base/variables/headers/budget/parameters'
import budgetParametersMargeHeader from '@base/variables/headers/budget/parameters-marge'
const ParametersBudgetProvider: FC = ({ children }) => {
  const toast = useToast()

  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const filtersList: ILayoutFilter[] = reportFilter

  const [filename, setFilename] = useState('Orçamento Parâmetros - Relatório')
  const [typeGrid, setTypeGrid] = useState(1)
  const [textConclusionUpdate, setTextConclusionUpdate] = useState('')
  const [data, updateData] = useImmer<any[]>([])
  const [dataMarge, updateDataMarge] = useImmer<any[]>([])
  const [dataMargeUpdate, updateDataMargeUpdate] = useImmer<any[]>([])
  const [dataBtLeo, updateDataBtLeo] = useImmer<any[]>([])
  const [query, setQuery] = useState('')
  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])

  const [params, updateParams] = useImmer<any>(reportRequest)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    budgetCommercialHeader
  )
  const [headersMarge, _updateHeadersMarge] = useImmer<ITableHeader[]>(
    budgetParametersMargeHeader
  )

  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsFormatted = { ...reportRequest }

    return paramsFormatted
  })

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })

    const paramsToSendFormatted = paramsReportToSendMapping({
      name,
      value,
      compareName: name,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)
    if (localStorage.getItem('budgetParametersParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        localStorage.getItem('budgetParametersParams') || ''
      )

      localStorage.setItem(
        'budgetParametersParams',
        JSON.stringify({ ...newParams, [name]: value })
      )
    }
    if (localStorage.getItem('budgetParametersParams')) {
      localStorage.setItem(
        'budgetParametersParams',
        JSON.stringify(paramsToSendFormatted)
      )
    }
  }

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('budgetParametersParams')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetParametersParams') || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem('budgetParametersParams', JSON.stringify(params))
    }
    if (localStorage.getItem('budgetParametersParams')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetParametersParams') || ''
      )
      setParamsToSend(newParams)
    } else {
      localStorage.setItem(
        'budgetParametersParams',
        JSON.stringify(paramsToSend)
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show
      }

      updateHeaders(newHeaders)
    },
    [headers, updateHeaders]
  )
  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return []
  }, [])

  const handlePageChange = useCallback(
    async (_page: number) => {
      setLoading(true)

      localStorage.setItem(
        '@AlertPurchaseParamsToSend:',
        JSON.stringify(paramsToSend)
      )

      budget
        .getBudgetReport(paramsToSend)
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => updateData([]))
      setLoading(false)
    },
    [paramsToSend, updateData, updatePagination]
  )
  const getData = useCallback(async () => {
    setLoading(true)

    budget
      .getBudgetParameters()
      .then(({ data: { data } }) => {
        updateData(draft => {
          return draft.filter(item => item.status === 99).concat(data)
        })
        setFilters.toggle()
        setLoading(false)
        setFilters.toggle()
      })
      .catch(() => {
        updateData([])
        setLoading(false)
        setFilters.toggle()
      })
  }, [setFilters, updateData])

  const getDataMargem = useCallback(async () => {
    setLoading(true)

    budget
      .getMargemMediaParameter()
      .then(({ data: { data } }) => {
        updateDataMarge(draft => {
          return draft.filter(item => item.status === 99).concat(data)
        })
        setLoading(false)
      })
      .catch(() => {
        updateDataMarge([])
        setLoading(false)
      })
  }, [setLoading, updateDataMarge])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        isMulti: false
      }
    ]
  }, [])

  const exportDataConsolidated = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 9999,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const obj = {
      ...paramsToSend,
      visualizacao: {
        btLeoLiquido: true,
        vendaLiquida: false,
        vendaBruta: false,
        margem: false
      },
      dimensao: {
        total: true,
        du: false
      },
      pagination
    }
    try {
      const { data: res } = await budget.postExportConsolidated(obj)
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [paramsToSend, pagination, filename, toast])

  const exportDataAnalitc = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const obj = {}
    try {
      const { data: res } = await budget.postExportAnalitc(obj)
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [toast, filename])

  const updateBadge = useCallback(async () => {
    setLoading(true)

    budget
      .postUpdateBaseBudget()
      .then(({ data: { _data } }) => {
        setLoading(false)
        setTextConclusionUpdate('Base de orçamento carregada com sucesso!')
      })
      .catch(() => {
        setLoading(false)
      })
  }, [setLoading, setTextConclusionUpdate])

  const saveUpdateBtLeo = useCallback(async () => {
    setLoading(true)
    const uniqueDataBtLeo = dataBtLeo.filter(
      (val, id, array) => array.indexOf(val) === id
    )
    budget
      .putBudgetParameters({ alterarBtLeo: uniqueDataBtLeo })
      .then(({ data: { _data } }) => {
        setLoading(false)
        toast({
          title: 'Sucesso.',
          description: 'Salvo edição em BT LEO',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      })
      .catch(() => {
        setLoading(false)
      })
  }, [setLoading, dataBtLeo, toast])

  const saveUpdateMarge = useCallback(async () => {
    setLoading(true)
    const uniqueDataMarge = dataMargeUpdate.filter(
      (val, id, array) => array.indexOf(val) === id
    )
    budget
      .putMargemMediaParameter({ margemMedia: uniqueDataMarge })
      .then(({ data: { _data } }) => {
        setLoading(false)
        toast({
          title: 'Sucesso.',
          description: 'Salvo edição em Calculo Margem Media',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      })
      .catch(() => {
        setLoading(false)
      })
  }, [setLoading, dataMargeUpdate, toast])

  const handledTypeGrid = (value: number) => {
    setTypeGrid(value)
  }

  const handleBtLeo = useCallback(
    async (id: number, wanted: number | string) => {
      const obj = {
        id: id,
        taxaConversao: wanted
      }
      updateDataBtLeo([...dataBtLeo, obj])
    },
    [dataBtLeo, updateDataBtLeo]
  )

  const handleMarge = useCallback(
    async (id: number, wanted: number | string) => {
      const obj = {
        id: id,
        margem: wanted
      }
      updateDataMargeUpdate([...dataMargeUpdate, obj])
    },
    [dataMargeUpdate, updateDataMargeUpdate]
  )

  return (
    <ParametersBudgetContext.Provider
      value={{
        loading,

        filtersList,
        pagination,
        tableInteraction,
        dataBtLeo,
        hiddenColumns,
        handleBtLeo,
        data: parseParametersBudgetData(data),
        actions,
        dataMarge: parseParametersMargeBudgetData(dataMarge),

        typeGrid,
        handledTypeGrid,
        params,
        textConclusionUpdate,
        handlePageChange,

        headers,
        filename,
        filters,
        query,
        updateBadge,
        setFilename,
        exportDataConsolidated,
        saveUpdateBtLeo,
        exportDataAnalitc,
        saveUpdateMarge,
        handleMarge,
        dataMargeUpdate,
        setQuery,
        headersMarge,
        toggleColumn,
        handleUpdateParams,
        getDataMargem,
        handleLocalParams,
        getData,

        setFilters
      }}
    >
      {children}
    </ParametersBudgetContext.Provider>
  )
}

export default ParametersBudgetProvider
export const ParametersBudgetContext = createContext<IBudgetParametersContext>(
  {} as IBudgetParametersContext
)
